import * as Ably from 'ably';
import { createAblyClient, getChannel } from './shared/ably.js';
import { renderChart } from './shared/chart.js';
import { renderController, clearControllerClasses } from './shared/controller.js';
import { voterClientId } from './shared/clientId.js';
import { showError } from './shared/errors.js';
import { mountLocationForm, loadCountryNameLookup, type Location, type MountedLocationForm } from './shared/location-form.js';
import { decodeLocationParams, SESSION_KEY, UNVERIFIED_KEY } from './shared/location-url.js';
import { isValidSessionId } from './shared/sessionId.js';
import { createSerialGate } from './shared/serial.js';
import { startCountdownOverlay, stopCountdown } from './shared/countdown.js';
import {
  VOTE_ANNOTATION_TYPE,
  SUGGESTION_ANNOTATION_TYPE,
  CELEBRATE_ANNOTATION_TYPE,
  VoterState,
  PollMessage,
  PollType,
  ControlMessage,
  PollOption,
  VerifiedUser,
  isControllerKind,
} from './shared/types.js';

let state: VoterState = 'auth';
let channel: Ably.RealtimeChannel;
let clientId: string;
let currentPollOptions: PollOption[] = [];
let currentPollType: PollType = 'list';
let currentPollSerial: string | null = null;
let currentPollId: number | null = null;
let currentSummary: Ably.SummaryUniqueValues | null = null;
let myVote: string | null = null;
let pollClosed = false;
// Absent from the poll message means the showrunner placed no restriction, so
// this only ever goes false on an explicit false.
let allowVoteChanges = true;
const serials = createSerialGate();

let verifiedUser: VerifiedUser | null = null;
// Set on the "I don't have a Google account" path, where the fan types their
// own address. unverifiedCode comes from the QR's URL and is the showrunner's
// permission for that path to exist at all — without it the link stays hidden,
// and the server would refuse the registration anyway.
let unverifiedEmail: string | null = null;
let unverifiedCode: string | null = null;
let sessionId: string;
let venueLocation: Location | null = null;
let visitorLocationForm: MountedLocationForm | null = null;
let chosenName: string | null = null;
// Set when registration comes back saying this signup deserves a celebration.
// It can't be published there and then: annotations need a message serial, and
// registration finishes before we've connected. Flushed from handleMessage,
// which is where serials arrive.
let pendingWelcome: string | null = null;

const $ = (sel: string) => document.querySelector(sel) as HTMLElement;

function setState(newState: VoterState) {
  state = newState;
  document.body.dataset.state = newState;
  // Drives the same dimming that a closed poll gets: the options are still on
  // screen and one of them is still visibly yours, they just don't take a tap.
  document.body.toggleAttribute('data-locked', !allowVoteChanges && myVote !== null);
  const statusEl = document.getElementById('status-text');
  if (statusEl) {
    statusEl.textContent =
      newState === 'voted'
        ? allowVoteChanges
          ? 'Vote recorded! Tap another option to change your vote.'
          : 'Vote recorded! Your vote is final.'
      : newState === 'results' ? 'Voting closed.'
      : '';
  }
}

interface SavedSession {
  clientId: string;
  name: string;
}

const sessionKey = (sid: string) => `voter-session-${sid}`;
const voteKey = (sid: string, pollId: number) => `voter-vote-${sid}-${pollId}`;
const suggestionsKey = (sid: string, pollId: number) => `voter-suggestions-${sid}-${pollId}`;

// Voter state lives in localStorage rather than sessionStorage so identity
// survives the browser discarding the tab (common on phones mid-gig) and a
// second tab can't cast a second vote — but with a TTL per entry, swept on
// load, so nothing lingers long past the show.
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

function storageSet(key: string, value: string) {
  try {
    localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + STORAGE_TTL_MS }));
  } catch {}
}

function storageGet(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { v, exp } = JSON.parse(raw);
    if (typeof v !== 'string' || typeof exp !== 'number' || exp < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

function sweepExpiredStorage() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      // storageGet drops expired/malformed entries as a side effect.
      if (key?.startsWith('voter-')) storageGet(key);
    }
  } catch {}
}

function loadSavedSession(sid: string): SavedSession | null {
  try {
    const raw = storageGet(sessionKey(sid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadSavedVote(sid: string, pollId: number): string | null {
  return storageGet(voteKey(sid, pollId));
}

function saveVote(sid: string, pollId: number, optionId: string) {
  storageSet(voteKey(sid, pollId), optionId);
}

function loadSavedSuggestions(sid: string, pollId: number): string[] {
  try {
    const raw = storageGet(suggestionsKey(sid, pollId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function appendSavedSuggestion(sid: string, pollId: number, text: string) {
  const existing = loadSavedSuggestions(sid, pollId);
  existing.push(text);
  storageSet(suggestionsKey(sid, pollId), JSON.stringify(existing));
}

function removeSavedSuggestion(sid: string, pollId: number, text: string) {
  const existing = loadSavedSuggestions(sid, pollId);
  const idx = existing.indexOf(text);
  if (idx === -1) return;
  existing.splice(idx, 1);
  storageSet(suggestionsKey(sid, pollId), JSON.stringify(existing));
}

function connectAndWait(name: string, existingClientId?: string) {
  clientId = existingClientId ?? voterClientId(name);
  storageSet(sessionKey(sessionId), JSON.stringify({ clientId, name }));

  $('#display-name').textContent = name;

  const ably = createAblyClient(clientId, sessionId, 'voter');
  channel = getChannel(ably, sessionId, 'voter');
  channel.subscribe(handleMessage);
  setState('waiting');
}

function handleMessage(msg: Ably.Message) {
  if (msg.name === 'poll') {
    const data = msg.data as PollMessage;
    const summary = msg.annotations?.summary?.[VOTE_ANNOTATION_TYPE] as Ably.SummaryUniqueValues | undefined;

    if (msg.serial !== currentPollSerial) {
      // New poll (or rewound message after refresh).
      if (!serials.admit(msg.serial, msg.version?.serial)) return;

      currentPollOptions = data.options;
      currentPollType = data.type ?? 'list';
      currentPollSerial = msg.serial!;
      currentPollId = data.pollId;
      currentSummary = summary ?? null;
      pollClosed = data.status === 'closed';
      allowVoteChanges = data.allowVoteChanges !== false;

      if (currentPollType === 'suggest') {
        myVote = null;
        renderSuggestPoll(data);
        setState('suggesting');
        setSuggestFormDisabled(pollClosed);
      } else {
        myVote = loadSavedVote(sessionId, data.pollId);
        renderPoll(data);
        if (myVote) highlightVote(myVote);
        setState(pollClosed ? 'results' : myVote ? 'voted' : 'voting');
      }
      // Loading mid-countdown: the rewound message says 'closing', so this
      // phone joins the count part-way through. It'll be a beat behind the
      // room, and the close that lands at the end snaps it straight.
      syncCountdown(data.status);
    } else {
      // Another version of the poll we're already showing: the admin starting
      // or cancelling the closing countdown, the close itself, or an annotation
      // summary update. A closed suggest poll carries no summary, so none of
      // these can be gated on there being one.
      if (serials.freshData(msg.serial, msg.version?.serial)) {
        if (data.status === 'closed' && !pollClosed) closeCurrentPoll();
        syncCountdown(data.status);
        allowVoteChanges = data.allowVoteChanges !== false;
      }
      if (summary && currentPollType !== 'suggest') {
        currentSummary = summary;
        renderResults();
      }
    }
  } else if (msg.name === 'control') {
    const data = msg.data as ControlMessage;
    serials.mark(msg.serial);
    if (data.action === 'end') {
      clearVoterStorage(sessionId);
      stopCountdown();
      setState('ended');
      return;
    }
    // 'reset' (admin jumped back to the start) and 'show-qr' (admin cleared the
    // poll) both mean there's no longer a poll to show.
    if (data.action === 'reset' || data.action === 'show-qr') {
      currentPollSerial = null;
      currentPollId = null;
      currentPollOptions = [];
      currentSummary = null;
      myVote = null;
      pollClosed = false;
      stopCountdown();
      setState('waiting');
    }
  }

  if (pendingWelcome) flushWelcome();
}

// The celebration is an annotation, so it needs some message to hang off — any
// will do, since the presenter only reads the name. Waiting for one is why this
// is deferred rather than published at signup: a fan who joins before the
// showrunner has published anything gets their fireworks when the show starts.
function flushWelcome() {
  const serial = serials.newest();
  if (!serial || !pendingWelcome) return;
  const names = pendingWelcome;
  // Cleared before the publish, not after, so two messages landing together
  // can't both send it.
  pendingWelcome = null;
  channel.annotations
    .publish(serial, { type: CELEBRATE_ANNOTATION_TYPE, name: names })
    .catch((err) => console.error('Failed to publish welcome:', err));
}

// Idempotent in both directions, which matters because annotation summary
// updates re-deliver the whole poll payload — including its 'closing' status —
// every time a vote lands during the countdown. Voting stays open throughout;
// the overlay is click-through so the last-second change of heart still works.
function syncCountdown(status: PollMessage['status']) {
  if (status === 'closing' && !pollClosed) {
    startCountdownOverlay();
  } else {
    stopCountdown();
  }
}

// Voting is over, but the poll stays on screen: final results for a vote poll,
// a disabled form for a suggest poll.
function closeCurrentPoll() {
  pollClosed = true;
  if (currentPollType === 'suggest') {
    setSuggestFormDisabled(true);
  } else {
    setState('results');
  }
}

function clearVoterStorage(sid: string) {
  try {
    localStorage.removeItem(sessionKey(sid));
    const prefixes = [`voter-vote-${sid}-`, `voter-suggestions-${sid}-`];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) localStorage.removeItem(key);
    }
  } catch {}
}

function renderSuggestPoll(data: PollMessage) {
  $('#suggest-question').textContent = data.question;
  const input = $('#suggest-input') as HTMLInputElement;
  input.value = '';
  renderMySuggestions(loadSavedSuggestions(sessionId, data.pollId));
}

function renderMySuggestions(items: string[]) {
  const list = $('#my-suggestions');
  const heading = $('#my-suggestions-heading');
  list.innerHTML = '';
  for (const text of items) {
    const li = document.createElement('li');
    const label = document.createElement('span');
    label.className = 'my-suggestion-text';
    label.textContent = text;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'my-suggestion-remove';
    remove.setAttribute('aria-label', `Remove suggestion “${text}”`);
    remove.textContent = '✕';
    remove.addEventListener('click', () => deleteSuggestion(text));
    li.appendChild(label);
    li.appendChild(remove);
    list.appendChild(li);
  }
  heading.classList.toggle('hidden', items.length === 0);
}

function setSuggestFormDisabled(disabled: boolean) {
  const input = $('#suggest-input') as HTMLInputElement;
  const submit = ($('#suggest-form') as HTMLFormElement).querySelector('button') as HTMLButtonElement;
  input.disabled = disabled;
  submit.disabled = disabled;
  if (disabled) input.placeholder = 'Suggestions closed';
  else input.placeholder = 'Type a suggestion…';
}

async function castSuggestion(text: string) {
  if (!currentPollSerial || currentPollId === null || pollClosed) return;
  try {
    await channel.annotations.publish(currentPollSerial, {
      type: SUGGESTION_ANNOTATION_TYPE,
      name: text,
    });
    appendSavedSuggestion(sessionId, currentPollId, text);
    renderMySuggestions(loadSavedSuggestions(sessionId, currentPollId));
  } catch (err: any) {
    showError(`Failed to submit: ${err.message || err}`);
  }
}

async function deleteSuggestion(text: string) {
  if (!currentPollSerial || currentPollId === null || pollClosed) return;
  try {
    // Deletes are keyed by (clientId, name): Ably stamps our own clientId on
    // the delete, so subscribers only ever drop the suggestion we published.
    await channel.annotations.delete(currentPollSerial, {
      type: SUGGESTION_ANNOTATION_TYPE,
      name: text,
    });
    removeSavedSuggestion(sessionId, currentPollId, text);
    renderMySuggestions(loadSavedSuggestions(sessionId, currentPollId));
  } catch (err: any) {
    showError(`Failed to remove: ${err.message || err}`);
  }
}

function renderControllerForVoter(selectedId: string | null) {
  if (!isControllerKind(currentPollType)) return;
  // Abxy uses SNES-style face-button colours by default, and inverts the
  // selected button to black-on-white so "your vote" stays unmistakable
  // against the surrounding colour. Dpad and the lr* kinds keep the original
  // outline-by-default look.
  const isAbxy = currentPollType === 'abxy';
  renderController(currentPollType, $('#options'), currentPollOptions, currentSummary, {
    clickable: true,
    onClick: castVote,
    selectedId,
    showHeatmap: false,
    showLeaderBadge: false,
    defaultFilled: isAbxy,
    invertSelected: isAbxy,
    display: 'percent',
    locked: !allowVoteChanges && selectedId !== null,
  });
}

function renderPoll(data: PollMessage) {
  $('#question').textContent = data.question;

  const optionsEl = $('#options');
  const chartEl = $('#chart');

  if (isControllerKind(currentPollType)) {
    chartEl.innerHTML = '';
    chartEl.classList.add('hidden');
    optionsEl.innerHTML = '';
    optionsEl.classList.remove('options-grid');
    renderControllerForVoter(myVote);
  } else {
    chartEl.classList.remove('hidden');
    clearControllerClasses(optionsEl);
    optionsEl.classList.add('options-grid');
    optionsEl.innerHTML = '';
    for (const opt of data.options) {
      const btn = document.createElement('button');
      btn.className = 'vote-button';
      btn.dataset.optionId = opt.id;
      btn.textContent = opt.label;
      btn.addEventListener('click', () => castVote(opt.id));
      optionsEl.appendChild(btn);
    }
    renderChart(chartEl, currentPollOptions, currentSummary, 'percent');
  }
}

function renderResults() {
  if (isControllerKind(currentPollType)) {
    renderControllerForVoter(myVote);
  } else {
    renderChart($('#chart'), currentPollOptions, currentSummary, 'percent');
  }
}

function highlightVote(optionId: string) {
  if (isControllerKind(currentPollType)) {
    renderControllerForVoter(optionId);
  } else {
    document.querySelectorAll('.vote-button').forEach((btn) => {
      (btn as HTMLButtonElement).classList.toggle('selected', (btn as HTMLElement).dataset.optionId === optionId);
      (btn as HTMLButtonElement).disabled = !allowVoteChanges;
    });
  }
}

async function castVote(optionId: string) {
  if (!currentPollSerial || pollClosed) return;
  // The disabled buttons are the affordance; this is the enforcement, and it
  // also covers the moment between a vote landing and the re-render.
  if (!allowVoteChanges && myVote) return;

  try {
    await channel.annotations.publish(currentPollSerial, {
      type: VOTE_ANNOTATION_TYPE,
      name: optionId,
    });
    myVote = optionId;
    if (currentPollId !== null) saveVote(sessionId, currentPollId, optionId);
    highlightVote(optionId);
    setState('voted');
  } catch (err: any) {
    showError(`Failed to vote: ${err.message || err}`);
  }
}

// Resolves to the names to welcome when this signup is one the server wants a
// celebration for, and undefined otherwise. The server does the matching because
// it's the only side that knows the address on every sign-in path: the redirect
// providers hand back a token and nothing else.
async function registerSubscriber(name: string, location: Location | null): Promise<string | undefined> {
  // A provider sign-in gives us a token the server signed over an address it
  // verified; the no-account path gives us a typed address plus the
  // showrunner's code.
  const identity = verifiedUser
    ? { token: verifiedUser.token }
    : unverifiedEmail
      ? { email: unverifiedEmail, sessionId, code: unverifiedCode }
      : null;
  if (!identity) return;
  try {
    const res = await fetch('/api/register-subscriber', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...identity,
        name,
        country: location?.country,
        countryName: location?.countryName,
        city: location?.city,
        postcode: location?.postcode,
      }),
    });
    const { welcome } = await res.json();
    return typeof welcome === 'string' ? welcome : undefined;
  } catch (err) {
    // Joining the show matters more than the mailing list, so a failure here is
    // logged and swallowed rather than blocking the voter.
    console.error('Failed to register subscriber:', err);
  }
}

// Both sign-in paths land on the same screen; the fan who has no Google
// account has to supply the email a Google sign-in would have given us.
// `required` is toggled rather than fixed in the markup, since a hidden
// required field blocks the form from submitting at all.
function showNameScreen(withEmail: boolean) {
  const nameInput = $('#name-input') as HTMLInputElement;
  const emailInput = $('#email-input') as HTMLInputElement;
  $('#name-heading').textContent = withEmail ? 'Who are you?' : 'What should we call you?';
  emailInput.classList.toggle('hidden', !withEmail);
  emailInput.required = withEmail;
  $('#email-disclaimer').classList.toggle('hidden', !withEmail);
  // Deliberately blank rather than prefilled from the provider: the name the
  // fan picks to be called on stage isn't the one on their Google account.
  nameInput.value = '';
  setState('enter-name');
  nameInput.focus();
}

// Both providers hand us a credential only the server can check; it answers
// with the address it read out of that credential, plus the token registration
// needs. Nothing past this point cares which provider the fan used.
async function exchangeCredential(provider: 'google' | 'apple', body: Record<string, string>) {
  const label = provider === 'google' ? 'Google' : 'Apple';
  try {
    const res = await fetch(`/api/${provider}-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      showError(`${label} sign-in failed. Please try again.`);
      return;
    }
    verifiedUser = await res.json();
    // Flow: signed in → name → location → register + connect.
    showNameScreen(false);
  } catch (err) {
    showError(`${label} sign-in failed. Please try again.`);
  }
}

// Each provider's SDK loads from its own CDN with `async defer`, so it may or
// may not have arrived by the time we want to configure it.
function awaitScript(srcFragment: string, ready: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    if (ready()) {
      resolve();
      return;
    }
    const script = document.querySelector(`script[src*="${srcFragment}"]`);
    script?.addEventListener('load', () => resolve());
  });
}

// Facebook and Discord can't be done in the page — they need a client secret to
// turn their code into a profile — so these buttons hand the whole tab over and
// the server sends the fan back with the result in the URL fragment.
const REDIRECT_PROVIDERS: Record<string, { label: string; logo: string }> = {
  facebook: {
    label: 'Sign in with Facebook',
    logo: 'M16 8.05C16 3.6 12.42 0 8 0S0 3.6 0 8.05C0 12.07 2.93 15.4 6.75 16v-5.62H4.72V8.05h2.03V6.28c0-2.02 1.2-3.13 3.02-3.13.88 0 1.79.16 1.79.16v1.97h-1c-1 0-1.31.62-1.31 1.26v1.51h2.22l-.35 2.33H9.25V16C13.07 15.4 16 12.07 16 8.05Z',
  },
  discord: {
    label: 'Sign in with Discord',
    logo: 'M13.55 3.11A13.2 13.2 0 0 0 10.25 2c-.15.28-.32.66-.44.96a12.2 12.2 0 0 0-3.62 0c-.12-.3-.3-.68-.45-.96a13.2 13.2 0 0 0-3.3 1.11C.36 6.34-.22 9.48.07 12.58a13.3 13.3 0 0 0 4.05 2.1c.33-.45.62-.93.87-1.43a8.6 8.6 0 0 1-1.37-.67c.12-.08.23-.17.34-.26a9.5 9.5 0 0 0 8.08 0c.11.09.22.18.34.26-.44.26-.9.49-1.37.67.25.5.54.98.87 1.43a13.3 13.3 0 0 0 4.05-2.1c.34-3.6-.58-6.71-2.38-9.47ZM5.34 10.67c-.79 0-1.44-.73-1.44-1.62 0-.9.63-1.63 1.44-1.63.8 0 1.46.73 1.44 1.63 0 .89-.64 1.62-1.44 1.62Zm5.32 0c-.79 0-1.44-.73-1.44-1.62 0-.9.63-1.63 1.44-1.63.81 0 1.46.73 1.45 1.63 0 .89-.64 1.62-1.45 1.62Z',
  },
};

function renderRedirectProviders(names: string[]) {
  const container = $('#oauth-providers');
  for (const name of names) {
    const ui = REDIRECT_PROVIDERS[name];
    if (!ui) continue;
    const btn = document.createElement('button');
    btn.className = `oauth-btn oauth-btn--${name}`;
    btn.innerHTML =
      `<svg class="oauth-logo" viewBox="0 0 16 16" aria-hidden="true" focusable="false">` +
      `<path fill="currentColor" d="${ui.logo}"/></svg><span>${ui.label}</span>`;
    // The querystring holds the session, the venue and the showrunner's code,
    // none of which survive leaving the page — so send it along for the server
    // to sign into `state` and hand back on the way in.
    btn.addEventListener('click', () => {
      const r = encodeURIComponent(window.location.search.replace(/^\?/, ''));
      window.location.href = `/api/${name}/start?r=${r}`;
    });
    container.appendChild(btn);
  }
}

// The redirect providers land the fan back here with their result in the
// fragment. Consume it and scrub it from the URL, so a refresh doesn't replay a
// stale token and it never reaches the server or the back button.
function consumeAuthFragment() {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = hash.get('auth');
  const failed = hash.get('auth_error');
  if (!token && !failed) return;

  history.replaceState(null, '', window.location.pathname + window.location.search);

  if (failed) {
    showError(
      failed === 'facebook'
        ? "Facebook sign-in failed — it may not have shared an email address. Please try another option."
        : 'Sign-in failed. Please try again.',
    );
    return;
  }
  verifiedUser = { token: token! };
  showNameScreen(false);
}

async function initSignIn() {
  const res = await fetch('/api/config');
  const { googleClientId, appleServicesId, appleRedirectUri, redirectProviders } = await res.json();

  if (Array.isArray(redirectProviders)) renderRedirectProviders(redirectProviders);

  await awaitScript('accounts.google.com', () => typeof google !== 'undefined' && !!google.accounts);

  google.accounts.id.initialize({
    client_id: googleClientId,
    callback: (response: any) => exchangeCredential('google', { credential: response.credential }),
  });

  google.accounts.id.renderButton($('#google-signin-btn'), {
    theme: 'outline',
    size: 'large',
    width: 320,
    text: 'signin_with',
  });

  // Absent from /api/config until the Services ID and redirect URI are both
  // configured, so an unconfigured deploy just never offers the button.
  if (appleServicesId) await initAppleSignIn(appleServicesId, appleRedirectUri);
}

async function initAppleSignIn(clientId: string, redirectURI: string) {
  await awaitScript('appleid.cdn-apple.com', () => typeof AppleID !== 'undefined');

  // Only `email` is asked for: the fan's own name is typed on the next screen,
  // so there's nothing to do with the one on their Apple ID.
  AppleID.auth.init({ clientId, redirectURI, scope: 'email', usePopup: true });

  const btn = $('#apple-signin-btn');
  btn.classList.remove('hidden');
  btn.addEventListener('click', async () => {
    try {
      const { authorization } = await AppleID.auth.signIn();
      await exchangeCredential('apple', { credential: authorization.id_token });
    } catch (err: any) {
      // Dismissing the popup rejects as well — a cancellation, not a failure.
      if (err?.error !== 'popup_closed_by_user') {
        showError('Apple sign-in failed. Please try again.');
      }
    }
  });
}

function setupLocationScreen() {
  const $liveNearby = $('#btn-live-nearby') as HTMLButtonElement;
  const $visiting = $('#btn-visiting') as HTMLButtonElement;
  const $visitingFields = $('#visiting-fields');
  const $continue = $('#btn-visiting-continue') as HTMLButtonElement;

  async function showVisitingForm() {
    $visitingFields.classList.remove('hidden');
    $liveNearby.classList.add('hidden');
    $visiting.classList.add('hidden');
    if (visitorLocationForm) return;
    try {
      // Default the country to the venue's country (when known) — most
      // "I'm visiting" voters are still from the same country as the show.
      visitorLocationForm = await mountLocationForm($('#visitor-location'), {
        idPrefix: 'visitor',
        countryLabel: 'My home country is…',
        cityLabel: 'My home city is…',
        zipLabel: 'My home ZIP code is…',
        initial: venueLocation?.country ? { country: venueLocation.country } : undefined,
        onChange: (loc) => { $continue.disabled = loc === null; },
      });
    } catch (err) {
      console.error('Failed to mount visitor location form:', err);
      showError('Could not load location selector. Please try refreshing.');
    }
  }

  const nearbyLabel = liveNearbyLabel(venueLocation);
  if (nearbyLabel) {
    $liveNearby.classList.remove('hidden');
    $liveNearby.textContent = nearbyLabel;
  } else {
    // No venue info reached us (manual joiner — typed the URL with just the
    // session). The "live nearby vs visiting" chooser has nothing to choose
    // between, so go straight to the country/city form.
    showVisitingForm();
  }

  $liveNearby.addEventListener('click', async () => {
    if (!chosenName) return;
    pendingWelcome = (await registerSubscriber(chosenName, venueLocation)) ?? null;
    connectAndWait(chosenName);
  });

  $visiting.addEventListener('click', showVisitingForm);

  $continue.addEventListener('click', async () => {
    const loc = visitorLocationForm?.getLocation() ?? null;
    if (!loc || !chosenName) return;
    pendingWelcome = (await registerSubscriber(chosenName, loc)) ?? null;
    connectAndWait(chosenName);
  });
}

function liveNearbyLabel(loc: Location | null): string | null {
  if (!loc) return null;
  if (loc.city) return `I live near ${loc.city}, ${loc.countryName}`;
  if (loc.country === 'US' && loc.postcode) return `I live in the US, near here?`;
  return null;
}

// Back navigation through the login funnel: location → name → auth. The typed
// name and any location selection are preserved (the form is mounted once and
// kept in visitorLocationForm), so stepping back and forward doesn't lose them.
function goBack() {
  if (state === 'enter-name') {
    // Returning to the very start: drop whichever identity got us here, so
    // coming back down the other path doesn't silently reuse it.
    verifiedUser = null;
    unverifiedEmail = null;
    setState('auth');
    return;
  }
  if (state === 'location') {
    // If the "I'm visiting" form is open and there's still a nearby/visiting
    // chooser to return to, step back to that chooser rather than all the way
    // to name entry.
    const visitingFields = $('#visiting-fields');
    const formOpen = !visitingFields.classList.contains('hidden');
    if (formOpen && liveNearbyLabel(venueLocation) !== null) {
      visitingFields.classList.add('hidden');
      $('#btn-live-nearby').classList.remove('hidden');
      $('#btn-visiting').classList.remove('hidden');
      return;
    }
    setState('enter-name');
    ($('#name-input') as HTMLInputElement).focus();
  }
}

async function init() {
  sweepExpiredStorage();

  const disclaimer = ($('#marketing-disclaimer-text') as HTMLTemplateElement).content.textContent ?? '';
  document.querySelectorAll('.marketing-disclaimer').forEach((el) => {
    el.textContent = disclaimer;
  });

  const params = new URLSearchParams(window.location.search);
  sessionId = params.get(SESSION_KEY) ?? '';
  if (!sessionId) {
    setState('no-session');
    return;
  }
  if (!isValidSessionId(sessionId)) {
    setState('invalid-session');
    return;
  }
  unverifiedCode = params.get(UNVERIFIED_KEY);

  // Decode venue location from the QR's URL params — countries.json is needed
  // to translate the ISO code into a display name. If the fetch fails, treat
  // it as "no venue location" and the visitor flow still works.
  if (params.get('c')) {
    try {
      const nameOf = await loadCountryNameLookup();
      venueLocation = decodeLocationParams(params, nameOf);
    } catch (err) {
      console.error('Failed to decode venue location:', err);
    }
  }

  const suggestForm = $('#suggest-form') as HTMLFormElement;
  suggestForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $('#suggest-input') as HTMLInputElement;
    const text = input.value.trim();
    if (!text) return;
    castSuggestion(text);
    input.value = '';
    input.focus();
  });

  const saved = loadSavedSession(sessionId);
  if (saved) {
    connectAndWait(saved.name, saved.clientId);
    return;
  }

  initSignIn();
  setupLocationScreen();

  document.querySelectorAll('.back-link').forEach((btn) =>
    btn.addEventListener('click', goBack),
  );

  const noAccountBtn = $('#no-account-btn');
  noAccountBtn.classList.toggle('hidden', !unverifiedCode);
  noAccountBtn.addEventListener('click', () => showNameScreen(true));

  // Name entry → location screen. The actual register + connect happens after
  // the location screen is completed (so the registered name is the chosen
  // display name, and location data is included in the same payload).
  const form = $('#name-form') as HTMLFormElement;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = ($('#name-input') as HTMLInputElement).value.trim();
    if (!name) return;
    // The email field is only shown, and only required, on the no-Google path.
    const emailInput = $('#email-input') as HTMLInputElement;
    if (emailInput.required) unverifiedEmail = emailInput.value.trim();
    chosenName = name;
    setState('location');
  });

  // Last, so the name screen it may jump straight to is fully wired up.
  consumeAuthFragment();
}

init();
